import React, { memo, useState, useEffect, useCallback } from "react";
import { withNamespaces } from "react-i18next";
import MessageBox from "src/modules/data-protection/components/MessageBox";
import ReportIncidenService from "../../components/api/ReportIncidenService";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ContentState, EditorState, convertFromHTML } from "draft-js";
import getApiErrorMessage from "../../utils/getApiErrorMessage";

const MessageComponent = ({
    t,
    caseId,
    type,
    reportDetails,
    prefillHtml,
    onPrefillConsumed,
}) => {
    const [mentionSuggestionList, setMentionSuggestionList] = useState([]);
    const [editorState, setEditorState] = useState(EditorState.createEmpty());

    const isDeleted =
        reportDetails?.status === 5 || reportDetails?.is_deleted_content;
    const isReadOnly = reportDetails?.is_read_only_analyst || isDeleted;
    const isInternalTab = type === "internal";
    const showInternalAi = isInternalTab && !isReadOnly;

    const messageSettings = {
        messageListUrl: isInternalTab ? "report.listCommentInternal" : "report.listComment",
        sendMessageUrl: isInternalTab ? "report.addCommentInternal" : "report.addComment",
        attachmentDownloadUrl: "/report/attachment/{attachmentId}/download",
        attachmentUploadUrl: "report.uploadAttachment",
        attachmentDeleteUrl: "attachment",
        payloadId: "report_case_id",
        assetId: caseId,
        mention: {
            enable: true,
            suggestionList: mentionSuggestionList,
        },
    };

    const handleFetchCommunicationMentionSuggestionListQuery = useQuery({
        queryKey: ["fetch-communication-mention-suggestion-list-query", caseId],
        queryFn: async () => {
            const service = ReportIncidenService.getInstance();
            return await service.mentionCommentSearch(caseId, {
                query: "",
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: !!caseId,
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);
            toast(t("An error occurred while fetching analyst admins list."), {
                type: "error",
            });
        },
    });

    const generateInternalDraftMutation = useMutation({
        mutationFn: async () => {
            const service = ReportIncidenService.getInstance();
            return await service.generateDsrResponse(caseId, {
                target: "internal_draft",
                include_internal_comments: true,
            });
        },
        onSuccess: (data) => {
            const contentState = ContentState.createFromText(data?.body_text || "");
            setEditorState(EditorState.createWithContent(contentState));
            if (data?.retention_notes) {
                toast(`${t("Retention notes")}: ${data.retention_notes}`, {
                    type: "info",
                });
            }
            toast(t("Internal draft generated. Please review before posting."), {
                type: "success",
            });
        },
        onError: (error) => {
            toast(
                t(getApiErrorMessage(error, t("Failed to generate internal AI draft."))),
                { type: "error" }
            );
        },
    });

    const handleGenerateInternalDraft = useCallback(() => {
        generateInternalDraftMutation.mutate();
    }, [generateInternalDraftMutation]);

    useEffect(() => {
        if (type !== "message" || !prefillHtml) {
            return;
        }

        const blocksFromHTML = convertFromHTML(prefillHtml);
        if (blocksFromHTML?.contentBlocks) {
            const contentState = ContentState.createFromBlockArray(
                blocksFromHTML.contentBlocks,
                blocksFromHTML.entityMap
            );
            setEditorState(EditorState.createWithContent(contentState));
        }

        onPrefillConsumed?.();
    }, [prefillHtml, type, onPrefillConsumed]);

    useEffect(() => {
        const { data } = handleFetchCommunicationMentionSuggestionListQuery ?? {};
        if (data && Array.isArray(data)) {
            const list = data.map((item) => ({
                text: item.full_name,
                url: item.id,
                value: item.full_name,
            }));
            setMentionSuggestionList(list);
        }
    }, [handleFetchCommunicationMentionSuggestionListQuery.data]);

    if (
        handleFetchCommunicationMentionSuggestionListQuery.isLoading ||
        handleFetchCommunicationMentionSuggestionListQuery.isFetching ||
        !caseId
    ) {
        return (
            <div className="dt-field dt-skeleton" style={{ width: "100%", height: "320px" }}></div>
        );
    }

    return (
        <MessageBox
            settings={{
                ...messageSettings,
                headerOptions: {
                    displayFilters: true,
                    title: isInternalTab ? "Internal Communication" : "Contact Person",
                    tooltipText: isInternalTab
                        ? "Visible only to internal users. You can mention someone using '@'."
                        : "Attention: This message will be sent to the reporting person.",
                },
            }}
            generateAIResponse={handleGenerateInternalDraft}
            reportDetails={reportDetails}
            aiButtonDisabled={generateInternalDraftMutation.isLoading}
            editorState={editorState}
            hasAI={showInternalAi}
        />
    );
};

export default withNamespaces()(
    memo(MessageComponent)
);
